import { type ReactNode, type RefObject, useEffect, useId, useRef } from 'react';

export interface ModalProps {
  /** Whether the modal is showing. A closed modal keeps its contents mounted. */
  open: boolean;
  onClose: () => void;
  /** The heading, which also names the dialog. */
  title: string;
  /** The modal's own class, for its size. */
  className?: string;
  testId?: string;
  closeTestId?: string;
  /**
   * Where focus goes once it closes. Without it focus goes back to whatever had it when it opened,
   * which is the right place unless that control is gone by then.
   */
  returnFocus?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

/**
 * A native modal dialog: a backdrop, a heading with Close, and its contents.
 *
 * Its contents stay mounted while it is closed, so whatever they hold — a payment form's frame,
 * mounted once — is there unchanged when it opens again. Focus moves to Close when it opens, so a
 * keyboard reader is inside what just appeared rather than on the control behind it, and back to
 * whatever had it once it closes. Escape, Close and a click on the backdrop each ask `onClose`;
 * whether it closes is the owner's `open`.
 */
export const Modal = ({
  open,
  onClose,
  title,
  className,
  testId,
  closeTestId,
  returnFocus,
  children,
}: ModalProps) => {
  const dialog = useRef<HTMLDialogElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const headingId = useId();

  useEffect(() => {
    const sheet = dialog.current;
    if (!open || sheet === null) {
      return;
    }

    const opener = document.activeElement;
    const returnTo = returnFocus?.current ?? opener;
    if (typeof sheet.showModal === 'function') {
      sheet.showModal();
    } else {
      sheet.setAttribute('open', '');
    }
    close.current?.focus();

    return () => {
      if (typeof sheet.close === 'function') {
        sheet.close();
      } else {
        sheet.removeAttribute('open');
      }
      if (returnTo instanceof HTMLElement && returnTo.isConnected) {
        returnTo.focus();
      }
    };
  }, [open, returnFocus]);

  return (
    <dialog
      ref={dialog}
      aria-labelledby={headingId}
      aria-modal="true"
      className={className === undefined ? 'modal' : `modal ${className}`}
      data-test-id={testId}
      onClick={(event) => {
        if (event.target !== event.currentTarget) {
          return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        ) {
          onClose();
        }
      }}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
      }}
      role="dialog"
    >
      <div className="modal-head">
        <h2 id={headingId}>{title}</h2>
        <button type="button" data-test-id={closeTestId} onClick={onClose} ref={close}>
          Close
        </button>
      </div>
      {children}
    </dialog>
  );
};
